import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { API_USER_DISTRIBUTOR_URL, API_USER_URL } from '@Env/environment';
import {
  ConsentConfirmResponse,
  ConsentSignRequest,
  ConsentTemplateResponse,
  IUserInfo,
  IUserInfoResponse,
  User,
} from '../models/user';
import { BehaviorSubject, Observable } from 'rxjs';
import { Authorized, Login, RegConfirm, RegisterPhone, Entity, OgranizationInfoByInn } from '../models/login';
import { LocalStorageService } from './local-storage';
import { AuthSessionService } from './auth-session.service';
import { PostRegVerify, PostRequestSignUp, PostRequestSignUpNew } from '../models/request/post-auth';
import { SetPasswordRequestBody, VerifyUpdatePasswordRequestBody } from '../models/request/password-models';
import { IPageableList } from '../models/pageable';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private _currentUserToken: BehaviorSubject<string>;
  private _currentUser: User;
  private userRegisterInfo = {
    code: '',
    identifier: null as number | null,
    phoneNumber: '',
  };

  private readonly authSession = inject(AuthSessionService);

  constructor(
    private http: HttpClient,
    private localStorage: LocalStorageService,
    private router: Router
  ) {}

  getOrganizationInfo(inn: string) {
    return this.http.get<OgranizationInfoByInn>(`${API_USER_DISTRIBUTOR_URL.replace('api/v1', 'open/api/v1')}/organization/info/${inn}`);
  }

  get currentUser(): User {
    return this._currentUser;
  }

  get currentUserToken(): BehaviorSubject<string> {
    return this._currentUserToken;
  }

  get userRegisterInfoValue() {
    return this.userRegisterInfo;
  }

  set setUserRegisterInfo(value: { identifier: number; phoneNumber: string; code: string }) {
    this.userRegisterInfo.identifier = value.identifier;
    this.userRegisterInfo.phoneNumber = value.phoneNumber;
    this.userRegisterInfo.code = value.code;
  }

  set currentUser(value: User) {
    this._currentUser = value;
  }

  get token(): string {
    return this.localStorage.getItem('token');
  }

  login(user: Login): Observable<IUserInfo> {
    const params = {
      phone: '7' + user.phone,
      password: user.password,
    };
    return this.http.post<IUserInfo>(`${API_USER_URL}/token`, params).pipe(map((response: any) => this.mapUserInfo(response)));
  }

  getUserInfo(user: Login): Observable<IUserInfo> {
    return this.http.post<IUserInfo>(`${API_USER_URL}/token`, user).pipe(map((response: any) => this.mapUserInfo(response)));
  }

  refreshToken(refreshToken: string): Observable<IUserInfo> {
    return this.http
      .post<IUserInfo>(`${API_USER_URL}/token/refresh-token`, { refreshToken })
      .pipe(map((response: any) => this.mapUserInfo(response)));
  }

  getLegalEntities(token?: string): Observable<Array<Entity>> {
    let params = {};
    if (token) {
      params = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }

    return this.http.get<any>(`${API_USER_URL}/user/client`, params);
  }

  getLegalEntitiesV2(queryParams: Partial<{ page?: number; size?: number; search?: string }>): Observable<IPageableList<Entity>> {
    let params = new HttpParams();
    if (Number.isInteger(queryParams.page)) {
      params = params.set('page', String(queryParams.page));
    }
    if (queryParams.size) {
      params = params.set('size', String(queryParams.size));
    }
    if (queryParams.search) {
      params = params.set('search', queryParams.search);
    }
    return this.http.get<IPageableList<Entity>>(`${API_USER_URL.replace('/api/v1', '/api/v2')}/user/client`, { params });
  }

  checkExistPhoneNumber(phoneNumber: string): Observable<Authorized> {
    return this.http.post<Authorized>(`${API_USER_URL}/user/check/pre-authorization/${phoneNumber}`, {});
  }

  registerPhoneNumberNew(phoneNumber: string): Observable<RegisterPhone> {
    return this.http.get<RegisterPhone>(`${API_USER_URL}/registration/user-org`, { params: { phoneNumber } });
  }

  registerPhoneNumberConfirmNew(identifier: number): Observable<RegConfirm> {
    return this.http.post<RegConfirm>(`${API_USER_URL}/registration/user-org/confirm`, { identifier: identifier.toString() });
  }

  acceptCodeNew(obj: PostRegVerify) {
    return this.http.post<any>(`${API_USER_URL}/registration/user-org/verify`, { ...obj });
  }

  signupNew(obj: PostRequestSignUpNew): Observable<IUserInfo> {
    return this.http
      .post<IUserInfo | IUserInfoResponse>(`${API_USER_URL}/registration/user-org/signup`, { ...obj })
      .pipe(map((response) => this.mapUserInfo(response)));
  }

  registerPhoneNumber(phoneNumber: string): Observable<RegisterPhone> {
    return this.http.get<RegisterPhone>(`${API_USER_URL}/registration`, { params: { phoneNumber } });
  }

  registerPhoneNumberConfirm(identifier: number): Observable<RegConfirm> {
    return this.http.post<RegConfirm>(`${API_USER_URL}/registration/confirm`, { identifier: identifier.toString() });
  }

  acceptCode(obj: PostRegVerify) {
    return this.http.post<any>(`${API_USER_URL}/registration/verify`, { ...obj });
  }

  signup(obj: PostRequestSignUp): Observable<IUserInfo> {
    return this.http
      .post<IUserInfoResponse>(`${API_USER_URL}/registration/signup`, { ...obj })
      .pipe(map((response) => this.mapUserInfo(response)));
  }

  logout(): void {
    this.authSession.clearSession();
    this.localStorage.removeAll();
    try {
      localStorage.removeItem('market_token');
    } catch {
      /* ignore */
    }
    if (!this.router.url.includes('/login')) {
      void this.router.navigate(['/login'], { replaceUrl: true });
    }
  }

  updatePasswordRequest(phoneNumber: string): Observable<{ identifier: number }> {
    return this.http.post<{ identifier: number }>(`${API_USER_URL}/password`, { phoneNumber });
  }

  updatePasswordVerify(body: VerifyUpdatePasswordRequestBody): Observable<void> {
    return this.http.post<void>(`${API_USER_URL}/password/verify`, body);
  }

  setNewPassword(body: SetPasswordRequestBody): Observable<IUserInfo> {
    return this.http.post<IUserInfoResponse>(`${API_USER_URL}/password/set`, body).pipe(map((response) => this.mapUserInfo(response)));
  }

  private mapUserInfo(response: any): IUserInfo {
    return {
      accessToken: response.accessToken ?? response.access_token,
      refreshToken: response.refreshToken ?? response.refresh_token,
      accessTokenExpires: response.accessTokenExpires,
      refreshTokenExpires: response.refreshTokenExpires,
      user: response.user,
    } as IUserInfo;
  }

  getUser(userId: number): Observable<User> {
    return this.http.get<User>(`${API_USER_URL}/user/${userId}`);
  }

  setClient(clientId: number, token?: string): Observable<IUserInfo> {
    const options = token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : {};

    return this.http
      .post<IUserInfo>(`${API_USER_URL}/user/client/change`, { clientId }, options)
      .pipe(map((response: any) => this.mapUserInfo(response)));
  }

  createParticipant(body: unknown): Observable<any> {
    return this.http.post(`${API_USER_URL}/participant`, body);
  }

  getCreateParticipantResultCode(participant: { doc_status?: string }): 'success' | 'alreadyExists' | 'orgNotFound' {
    switch (participant.doc_status) {
      case 'IP_OK':
      case 'OOO_OK':
      case 'OOO_ORGANIZATION_NOT_FOUND':
      case 'PPU_OK':
        return 'success';
      case 'ALREADY_EXISTS':
        return 'alreadyExists';
      case 'PPU_ORGANIZATION_NOT_FOUND':
        return 'orgNotFound';
      default:
        return 'orgNotFound';
    }
  }

  sendSmsCodeParticipant(body: unknown): Observable<any> {
    return this.http.post(`${API_USER_URL}/participant/confirm`, body);
  }

  getConsentTemplate(accessToken: string, inn?: string): Observable<ConsentTemplateResponse> {
    let params = new HttpParams();

    if (inn) {
      params = params.set('inn', inn);
    }

    return this.http.get<ConsentTemplateResponse>(`${API_USER_URL}/consent/template`, {
      params,
      headers: this.createAuthHeaders(accessToken),
    });
  }

  confirmConsent(accessToken: string): Observable<ConsentConfirmResponse> {
    return this.http.post<ConsentConfirmResponse>(
      `${API_USER_URL}/consent/confirm`,
      {},
      {
        headers: this.createAuthHeaders(accessToken),
      }
    );
  }

  signConsent(accessToken: string, body: ConsentSignRequest): Observable<void> {
    return this.http.post<void>(`${API_USER_URL}/consent/sign`, body, {
      headers: this.createAuthHeaders(accessToken),
    });
  }

  private createAuthHeaders(accessToken: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${accessToken}`,
    });
  }
}
